/**
 * Register WooCommerce Integration
 * Registers the WooCommerce integration with the integration registry
 */

import { IntegrationRegistry } from '../registry/IntegrationRegistry';
import { WooCommerceIntegrationFactory } from './WooCommerceIntegrationFactory';
import { IntegrationConfig } from '../../../types/integration';

export function registerWooCommerceIntegration(): void {
  const registry = IntegrationRegistry.getInstance();
  registry.register(
    'woocommerce',
    'WooCommerce',
    'ecommerce',
    'woocommerce',
    '1.0.0',
    {
      create: async (config: IntegrationConfig) => {
        return WooCommerceIntegrationFactory.fromConfig(config);
      },
      validate: async (config: Partial<IntegrationConfig>) => {
        const validation = WooCommerceIntegrationFactory.validateParams(config as any);
        return {
          valid: validation.valid,
          errors: validation.errors.map(error => ({
            field: 'config',
            message: error,
            code: 'VALIDATION_ERROR'
          })),
          warnings: []
        };
      },
      getConfigSchema: () => ({
        type: 'object',
        required: ['siteUrl', 'consumerKey', 'consumerSecret'],
        properties: {
          siteUrl: {
            type: 'string',
            title: 'Site URL',
            description: 'Your WooCommerce store URL (e.g., https://yourstore.com)',
            format: 'uri',
            examples: ['https://yourstore.com', 'https://shop.example.com']
          },
          consumerKey: {
            type: 'string',
            title: 'Consumer Key',
            description: 'WooCommerce REST API consumer key',
            minLength: 10,
            examples: ['ck_1234567890abcdef1234567890abcdef12345678']
          },
          consumerSecret: {
            type: 'string',
            title: 'Consumer Secret',
            description: 'WooCommerce REST API consumer secret',
            minLength: 10,
            format: 'password',
            examples: ['cs_1234567890abcdef1234567890abcdef12345678']
          },
          apiVersion: {
            type: 'string',
            title: 'API Version',
            description: 'WooCommerce REST API version to use',
            enum: ['v1', 'v2', 'v3'],
            default: 'v3'
          },
          useSSL: {
            type: 'boolean',
            title: 'Use SSL',
            description: 'Use SSL for API requests (recommended)',
            default: true
          },
          timeout: {
            type: 'integer',
            title: 'Request Timeout',
            description: 'Request timeout in milliseconds',
            minimum: 1000,
            maximum: 300000,
            default: 30000
          },
          webhookEndpoint: {
            type: 'string',
            title: 'Webhook Endpoint',
            description: 'URL to receive webhook notifications (optional)',
            format: 'uri',
            examples: ['https://yourapp.com/webhooks/woocommerce']
          },
          webhookSecret: {
            type: 'string',
            title: 'Webhook Secret',
            description: 'Secret for webhook signature verification (optional)',
            format: 'password'
          }
        }
      }),
      getSupportedFeatures: () => [
        'product_sync',
        'customer_sync',
        'order_sync',
        'cart_abandonment',
        'inventory_tracking',
        'product_recommendations',
        'webhooks',
        'revenue_attribution',
        'customer_segmentation'
      ]
    }
  );
}